import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faAward,
  faBookOpen,
  faBriefcase,
  faBug,
  faCalendarDays,
  faCertificate,
  faCircleCheck,
  faCircleExclamation,
  faClock,
  faDownload,
  faEnvelope,
  faFileLines,
  faFloppyDisk,
  faGlobe,
  faGraduationCap,
  faIdCard,
  faLaptop,
  faRotateRight,
  faTriangleExclamation,
  faUser,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../../services/api';
import './AdminUserProfile.css';

const safeList = (value) => Array.isArray(value) ? value : [];

const dateTime = (value, includeTime = true) => {
  if (!value) return 'Não registrado';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Não registrado';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(parsed);
};

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const statusLabel = (status) => ({
  completed: 'Aprovado',
  pending: 'Pendente',
  failed: 'Recusado',
  cancelled: 'Cancelado',
  refunded: 'Estornado',
  in_progress: 'Em andamento',
  draft: 'Rascunho',
  ready: 'Pronto',
  generated: 'Gerado',
}[status] || status || 'Não informado');

const Status = ({ value }) => (
  <span className={`aup-status aup-status-${value || 'empty'}`}>{statusLabel(value)}</span>
);

const Empty = ({ children }) => <div className="aup-empty">{children}</div>;

const InfoItem = ({ icon, label, value }) => (
  <div className="aup-info-item">
    <FontAwesomeIcon icon={icon} />
    <span><small>{label}</small><strong>{value || 'Não informado'}</strong></span>
  </div>
);

const AdminUserProfile = () => {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('diagnostics');
  const [selectedResumeId, setSelectedResumeId] = useState(null);
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [downloadKey, setDownloadKey] = useState('');
  const [error, setError] = useState('');
  const [registrationForm, setRegistrationForm] = useState({
    name: '', email: '', cpf: '', rg: '', country: '',
  });
  const [registrationErrors, setRegistrationErrors] = useState({});
  const [registrationMessage, setRegistrationMessage] = useState('');
  const [registrationSaving, setRegistrationSaving] = useState(false);

  const profileRegistration = (data) => ({
    name: data?.name || '',
    email: data?.email || '',
    cpf: data?.cpf || '',
    rg: data?.rg || '',
    country: data?.country || '',
  });

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.admin.user(userId);
      setProfile(response.data);
      setRegistrationForm(profileRegistration(response.data));
      setRegistrationErrors({});
      setRegistrationMessage('');
      const primary = safeList(response.data?.resumes).find((item) => item.is_primary)
        || safeList(response.data?.resumes)[0];
      setSelectedResumeId(primary?.id || null);
    } catch (requestError) {
      setError(requestError.response?.status === 404
        ? 'Usuário não encontrado.'
        : 'Não foi possível carregar o perfil do usuário.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (activeTab !== 'resume' || !selectedResumeId) return;

    let active = true;
    setResumeLoading(true);
    apiService.admin.userResume(userId, selectedResumeId)
      .then((response) => {
        if (active) setResume(response.data);
      })
      .catch(() => {
        if (active) setResume(null);
      })
      .finally(() => {
        if (active) setResumeLoading(false);
      });

    return () => { active = false; };
  }, [activeTab, selectedResumeId, userId]);

  const tabs = useMemo(() => [
    { id: 'diagnostics', label: 'Diagnóstico', icon: faBug, count: profile?.access_issues?.length },
    { id: 'registration', label: 'Cadastro', icon: faIdCard },
    { id: 'courses', label: 'Cursos', icon: faBookOpen, count: profile?.courses?.length },
    { id: 'certificates', label: 'Certificados', icon: faCertificate, count: profile?.certificates?.length },
    { id: 'resume', label: 'Currículo', icon: faFileLines, count: profile?.resumes?.length },
    { id: 'activity', label: 'Atividade técnica', icon: faLaptop, count: profile?.recent_errors?.length },
  ], [profile]);

  const saveBlob = async (key, request, fallbackName) => {
    try {
      setDownloadKey(key);
      const response = await request();
      const disposition = response.headers?.['content-disposition'] || '';
      const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
      const plainName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
      const filename = encodedName ? decodeURIComponent(encodedName) : (plainName || fallbackName);
      const url = window.URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      setError('Não foi possível gerar o documento. Confira se todos os requisitos estão completos.');
    } finally {
      setDownloadKey('');
    }
  };

  const updateRegistrationField = (event) => {
    const { name, value } = event.target;
    setRegistrationForm((current) => ({ ...current, [name]: value }));
    setRegistrationErrors((current) => ({ ...current, [name]: undefined }));
    setRegistrationMessage('');
  };

  const resetRegistrationForm = () => {
    setRegistrationForm(profileRegistration(profile));
    setRegistrationErrors({});
    setRegistrationMessage('');
  };

  const saveRegistration = async (event) => {
    event.preventDefault();
    try {
      setRegistrationSaving(true);
      setRegistrationErrors({});
      setRegistrationMessage('');
      const response = await apiService.admin.updateUser(userId, registrationForm);
      const updated = response.data.user;
      setProfile((current) => ({ ...current, ...updated }));
      setRegistrationForm(profileRegistration(updated));
      setRegistrationMessage(response.data.message || 'Cadastro atualizado com sucesso.');
    } catch (requestError) {
      const validationErrors = requestError.response?.data?.errors || {};
      setRegistrationErrors(validationErrors);
      if (Object.keys(validationErrors).length === 0) {
        setRegistrationErrors({ _form: ['Não foi possível atualizar o cadastro.'] });
        setRegistrationMessage('Não foi possível atualizar o cadastro. Tente novamente.');
      }
    } finally {
      setRegistrationSaving(false);
    }
  };

  if (loading) {
    return <main className="aup-page"><div className="aup-loading"><FontAwesomeIcon icon={faRotateRight} spin /> Carregando perfil...</div></main>;
  }

  if (!profile) {
    return (
      <main className="aup-page">
        <div className="aup-fatal">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <h1>{error || 'Perfil indisponível'}</h1>
          <Link to="/admin"><FontAwesomeIcon icon={faArrowLeft} /> Voltar ao painel</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="aup-page">
      <div className="aup-shell">
        <div className="aup-topbar">
          <Link to="/admin" className="aup-back"><FontAwesomeIcon icon={faArrowLeft} /> Painel administrativo</Link>
          <button type="button" onClick={loadProfile} className="aup-refresh"><FontAwesomeIcon icon={faRotateRight} /> Atualizar dados</button>
        </div>

        {error && <div className="aup-alert"><FontAwesomeIcon icon={faTriangleExclamation} /> {error}</div>}

        <header className="aup-hero">
          <div className="aup-avatar">{profile.name?.charAt(0)?.toUpperCase() || 'U'}</div>
          <div className="aup-identity">
            <span>Perfil operacional do usuário #{profile.id}</span>
            <h1>{profile.name}</h1>
            <p><FontAwesomeIcon icon={faEnvelope} /> {profile.email}</p>
          </div>
          <div className="aup-last-access">
            <small>Último acesso identificado</small>
            <strong>{dateTime(profile.last_seen_at)}</strong>
            <span>{profile.last_access
              ? `${profile.last_access.device || 'dispositivo'} · ${profile.last_access.browser || 'navegador'} · ${profile.last_access.operating_system || 'sistema'}`
              : 'Ainda sem telemetria de navegação'}</span>
          </div>
        </header>

        <section className="aup-stats" aria-label="Resumo do usuário">
          <div><FontAwesomeIcon icon={faBookOpen} /><span><strong>{profile.stats?.courses || 0}</strong><small>cursos</small></span></div>
          <div><FontAwesomeIcon icon={faCircleCheck} /><span><strong>{profile.stats?.completed_courses || 0}</strong><small>concluídos</small></span></div>
          <div><FontAwesomeIcon icon={faAward} /><span><strong>{profile.stats?.certificates || 0}</strong><small>certificados</small></span></div>
          <div><FontAwesomeIcon icon={faFileLines} /><span><strong>{profile.stats?.resumes || 0}</strong><small>currículos</small></span></div>
          <div className={profile.stats?.recorded_errors ? 'has-error' : ''}><FontAwesomeIcon icon={faBug} /><span><strong>{profile.stats?.recorded_errors || 0}</strong><small>erros registrados</small></span></div>
        </section>

        <div className="aup-workspace">
          <nav className="aup-tabs" aria-label="Seções do perfil">
            {tabs.map((tab) => (
              <button
                type="button"
                key={tab.id}
                className={activeTab === tab.id ? 'active' : ''}
                onClick={() => setActiveTab(tab.id)}
              >
                <FontAwesomeIcon icon={tab.icon} />
                <span>{tab.label}</span>
                {Number.isFinite(tab.count) && <small>{tab.count}</small>}
              </button>
            ))}
          </nav>

          <section className="aup-content">
            {activeTab === 'diagnostics' && (
              <div>
                <div className="aup-section-heading">
                  <div><span>Análise automática</span><h2>Possíveis impedimentos</h2></div>
                  <p>Sinais técnicos e operacionais encontrados nos dados do usuário.</p>
                </div>
                <div className="aup-issues">
                  {safeList(profile.access_issues).map((issue) => (
                    <button type="button" className={`aup-issue aup-issue-${issue.severity}`} key={issue.id} onClick={() => setActiveTab(issue.target)}>
                      <FontAwesomeIcon icon={issue.severity === 'critical' ? faTriangleExclamation : faCircleExclamation} />
                      <span><strong>{issue.title}</strong><p>{issue.description}</p><small>{dateTime(issue.occurred_at)}</small></span>
                      <b>Ver detalhes →</b>
                    </button>
                  ))}
                  {safeList(profile.access_issues).length === 0 && (
                    <div className="aup-all-clear"><FontAwesomeIcon icon={faCircleCheck} /><span><strong>Nenhum impedimento aparente</strong><p>Não há sinais recentes de erro, inconsistência ou abandono neste perfil.</p></span></div>
                  )}
                </div>
                <div className="aup-diagnostic-note">
                  <strong>Como interpretar</strong>
                  <p>Os alertas combinam erros reais de comunicação, estado dos pagamentos, tentativas de avaliação, inatividade e consistência dos certificados. Eles ajudam na investigação, mas não alteram a conta do aluno.</p>
                </div>
              </div>
            )}

            {activeTab === 'registration' && (
              <div>
                <div className="aup-section-heading">
                  <div><span>Dados da conta</span><h2>Cadastro do usuário</h2></div>
                  <p>Corrija os dados com atenção. A alteração do e-mail muda o endereço usado para acessar a conta.</p>
                </div>
                <form className="aup-registration-form" onSubmit={saveRegistration} noValidate>
                  <div className="aup-form-grid">
                    {[
                      ['name', 'Nome completo', 'text', faUser, 'Nome e sobrenome'],
                      ['email', 'E-mail', 'email', faEnvelope, 'usuario@exemplo.com'],
                      ['cpf', 'CPF', 'text', faIdCard, '000.000.000-00'],
                      ['rg', 'RG', 'text', faIdCard, 'Número do RG'],
                      ['country', 'País', 'text', faGlobe, 'Brasil'],
                    ].map(([name, label, type, icon, placeholder]) => (
                      <label className={`aup-form-field ${registrationErrors[name] ? 'has-error' : ''}`} key={name}>
                        <span><FontAwesomeIcon icon={icon} /> {label}</span>
                        <input
                          name={name}
                          type={type}
                          value={registrationForm[name]}
                          placeholder={placeholder}
                          onChange={updateRegistrationField}
                          autoComplete="off"
                          required={name === 'name' || name === 'email'}
                          aria-invalid={Boolean(registrationErrors[name])}
                          aria-describedby={registrationErrors[name] ? `aup-${name}-error` : undefined}
                        />
                        {registrationErrors[name] && (
                          <small id={`aup-${name}-error`} role="alert">{registrationErrors[name][0]}</small>
                        )}
                      </label>
                    ))}
                  </div>
                  <div className="aup-registration-meta">
                    <InfoItem icon={faCalendarDays} label="Conta criada" value={dateTime(profile.created_at)} />
                    <InfoItem icon={faCircleCheck} label="E-mail verificado" value={profile.email_verified_at ? dateTime(profile.email_verified_at) : 'Não verificado'} />
                    <InfoItem icon={faLaptop} label="Forma de acesso" value={profile.auth_provider === 'google' ? 'Google' : 'E-mail e senha'} />
                    <InfoItem icon={faClock} label="Cadastro atualizado" value={dateTime(profile.updated_at)} />
                  </div>
                  {registrationMessage && (
                    <div className={`aup-form-message ${Object.keys(registrationErrors).length ? 'error' : 'success'}`} role="status">
                      {registrationMessage}
                    </div>
                  )}
                  <div className="aup-form-actions">
                    <button type="button" className="secondary" onClick={resetRegistrationForm} disabled={registrationSaving}>
                      <FontAwesomeIcon icon={faXmark} /> Descartar alterações
                    </button>
                    <button type="submit" className="primary" disabled={registrationSaving}>
                      <FontAwesomeIcon icon={faFloppyDisk} /> {registrationSaving ? 'Salvando...' : 'Salvar cadastro'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'courses' && (
              <div>
                <div className="aup-section-heading"><div><span>Aprendizado</span><h2>Cursos e avaliações</h2></div><p>{safeList(profile.courses).length} matrícula(s)</p></div>
                <div className="aup-card-list">
                  {safeList(profile.courses).map((course) => (
                    <article className="aup-course-card" key={course.enrollment_id || course.id}>
                      <div className="aup-card-title"><div><span>Curso #{course.id}</span><h3>{course.title || 'Curso removido'}</h3></div><Status value={course.progress_status} /></div>
                      <div className="aup-progress"><span style={{ width: `${Math.min(course.progress_percent || 0, 100)}%` }} /></div>
                      <div className="aup-course-meta">
                        <span><strong>{course.progress_percent || 0}%</strong> de progresso</span>
                        <span><strong>{course.completed_modules || 0}/{course.total_modules || 0}</strong> módulos</span>
                        <span><strong>{course.score ?? '—'}</strong> nota</span>
                        <span><strong>{dateTime(course.last_activity_at)}</strong> última atividade</span>
                      </div>
                    </article>
                  ))}
                  {safeList(profile.courses).length === 0 && <Empty>Este usuário ainda não iniciou cursos.</Empty>}
                </div>
                {safeList(profile.assessments).length > 0 && (
                  <div className="aup-subsection"><h3>Histórico de avaliações</h3><div className="aup-table-wrap"><table><thead><tr><th>Curso</th><th>Nota</th><th>Resultado</th><th>Data</th></tr></thead><tbody>
                    {profile.assessments.map((assessment) => <tr key={assessment.id}><td>{assessment.curso?.title || 'Curso removido'}</td><td>{assessment.score ?? '—'}</td><td><Status value={assessment.passed ? 'completed' : 'failed'} /></td><td>{dateTime(assessment.created_at)}</td></tr>)}
                  </tbody></table></div></div>
                )}
              </div>
            )}

            {activeTab === 'certificates' && (
              <div>
                <div className="aup-section-heading"><div><span>Documentos emitidos</span><h2>Certificados</h2></div><p>Visualização administrativa fiel ao documento do aluno.</p></div>
                <div className="aup-certificate-list">
                  {safeList(profile.certificates).map((certificate) => (
                    <article className="aup-certificate" key={certificate.id}>
                      <div className="aup-certificate-icon"><FontAwesomeIcon icon={faCertificate} /></div>
                      <div><small>Emitido em {dateTime(certificate.issued_at, false)}</small><h3>{certificate.curso?.title || certificate.title}</h3><p>Nota {certificate.score ?? '—'} · conclusão {dateTime(certificate.completed_at, false)}</p><code>{certificate.credential_code || 'Código indisponível'}</code></div>
                      <div className="aup-document-actions">
                        {certificate.validation_url && <a href={certificate.validation_url} target="_blank" rel="noreferrer">Validar certificado</a>}
                        <button
                          type="button"
                          disabled={!certificate.download_available || downloadKey === `certificate-${certificate.id}`}
                          onClick={() => saveBlob(
                            `certificate-${certificate.id}`,
                            () => apiService.admin.downloadUserCertificate(profile.id, certificate.curso_id),
                            `certificado-${certificate.curso_id}.pdf`
                          )}
                        ><FontAwesomeIcon icon={faDownload} /> {downloadKey === `certificate-${certificate.id}` ? 'Gerando...' : 'Baixar PDF'}</button>
                      </div>
                    </article>
                  ))}
                  {safeList(profile.certificates).length === 0 && <Empty>Nenhum certificado foi emitido para este usuário.</Empty>}
                </div>
                {safeList(profile.payments).length > 0 && <div className="aup-subsection"><h3>Histórico financeiro relacionado</h3><div className="aup-table-wrap"><table><thead><tr><th>Curso</th><th>Valor</th><th>Status</th><th>Método</th><th>Data</th></tr></thead><tbody>
                  {profile.payments.map((payment) => <tr key={payment.id}><td>{payment.curso?.title || 'Curso removido'}</td><td>{currency.format(payment.amount || 0)}</td><td><Status value={payment.status} /></td><td>{payment.payment_method || '—'}</td><td>{dateTime(payment.created_at)}</td></tr>)}
                </tbody></table></div></div>}
              </div>
            )}

            {activeTab === 'resume' && (
              <div>
                <div className="aup-section-heading"><div><span>Perfil profissional</span><h2>Currículo</h2></div><p>Conteúdo salvo pelo próprio usuário.</p></div>
                {safeList(profile.resumes).length > 0 ? (
                  <>
                    <div className="aup-resume-picker">
                      {profile.resumes.map((item) => <button type="button" className={selectedResumeId === item.id ? 'active' : ''} key={item.id} onClick={() => setSelectedResumeId(item.id)}><span><strong>{item.title}</strong><small>Atualizado {dateTime(item.updated_at)}</small></span>{item.is_primary && <b>Principal</b>}</button>)}
                    </div>
                    {resumeLoading ? <div className="aup-loading-inline"><FontAwesomeIcon icon={faRotateRight} spin /> Carregando currículo...</div> : resume ? (
                      <article className="aup-resume-sheet">
                        <header><div><span>{resume.contact?.professional_title || resume.area || 'Perfil profissional'}</span><h2>{resume.contact?.full_name || profile.name}</h2><p>{[resume.contact?.city, resume.contact?.state].filter(Boolean).join(' - ')} {resume.contact?.email ? `· ${resume.contact.email}` : ''}</p></div><div><Status value={resume.status} /><button type="button" onClick={() => saveBlob(`resume-${resume.id}`, () => apiService.admin.downloadUserResume(profile.id, resume.id), `curriculo-${resume.id}.pdf`)} disabled={downloadKey === `resume-${resume.id}`}><FontAwesomeIcon icon={faDownload} /> {downloadKey === `resume-${resume.id}` ? 'Gerando...' : 'Baixar PDF'}</button></div></header>
                        {resume.professional_summary && <section><h3>Resumo profissional</h3><p>{resume.professional_summary}</p></section>}
                        {safeList(resume.skills).length > 0 && <section><h3>Competências</h3><div className="aup-tags">{resume.skills.map((skill) => <span key={skill}>{skill}</span>)}</div></section>}
                        {safeList(resume.experiences).length > 0 && <section><h3><FontAwesomeIcon icon={faBriefcase} /> Experiência</h3>{resume.experiences.map((experience) => <div className="aup-resume-entry" key={experience.id}><strong>{experience.role}</strong><span>{experience.company} · {experience.location}</span><small>{experience.start_month} — {experience.is_current ? 'atual' : experience.end_month}</small>{safeList(experience.achievements).map((achievement) => <p key={achievement}>• {achievement}</p>)}</div>)}</section>}
                        {safeList(resume.educations).length > 0 && <section><h3><FontAwesomeIcon icon={faGraduationCap} /> Formação</h3>{resume.educations.map((education) => <div className="aup-resume-entry" key={education.id}><strong>{education.course}</strong><span>{education.institution} · {education.level}</span><small>{education.status}</small></div>)}</section>}
                        {safeList(resume.external_certifications).length > 0 && <section><h3>Outras certificações</h3>{resume.external_certifications.map((item) => <div className="aup-resume-entry" key={item.id}><strong>{item.name}</strong><span>{item.institution} · {item.year}</span></div>)}</section>}
                        {!resume.professional_summary && safeList(resume.experiences).length === 0 && <Empty>Este currículo ainda está no início do preenchimento.</Empty>}
                      </article>
                    ) : <Empty>Não foi possível abrir este currículo.</Empty>}
                  </>
                ) : <Empty>O usuário ainda não criou um currículo.</Empty>}
              </div>
            )}

            {activeTab === 'activity' && (
              <div>
                <div className="aup-section-heading"><div><span>Rastreamento de suporte</span><h2>Erros e navegação recente</h2></div><p>Dados técnicos para reproduzir o contexto do usuário.</p></div>
                <div className="aup-subsection"><h3>Erros de API registrados</h3><div className="aup-technical-list">
                  {safeList(profile.recent_errors).map((item) => <article className="error" key={item.id}><FontAwesomeIcon icon={faBug} /><span><strong>{item.status || 'Falha de rede'} em {item.page_path}</strong><p>{item.message || 'Sem mensagem adicional'}</p><small>{dateTime(item.created_at)} · {item.device || 'dispositivo desconhecido'} · {item.browser || 'navegador desconhecido'} · {item.operating_system || 'sistema desconhecido'}</small></span></article>)}
                  {safeList(profile.recent_errors).length === 0 && <Empty>Nenhum erro técnico foi registrado para este usuário.</Empty>}
                </div></div>
                <div className="aup-subsection"><h3>Navegação recente</h3><div className="aup-technical-list">
                  {safeList(profile.recent_navigation).map((item) => <article key={item.id}><FontAwesomeIcon icon={faLaptop} /><span><strong>{item.page_path}</strong><p>{item.element_label || (item.event_type === 'page_view' ? 'Página visualizada' : 'Interação')}</p><small>{dateTime(item.created_at)} · {item.device_type || 'dispositivo desconhecido'} · {item.browser_name || 'navegador desconhecido'}</small></span></article>)}
                  {safeList(profile.recent_navigation).length === 0 && <Empty>Ainda não há navegação identificada para este usuário.</Empty>}
                </div></div>
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
};

export default AdminUserProfile;
